import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, HeartHandshake, Lightbulb, Megaphone, Rocket, Sparkles, Target } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTABlock } from "@/components/CTABlock";
import { SITE } from "@/constants/site";
import { useLang } from "@/i18n/LangContext";

const VALUE_ICONS = [Sparkles, Lightbulb, HeartHandshake];
const PARCOURS_ICONS = [Target, Rocket, Globe2, Megaphone];

export default function About() {
  const { t, l } = useLang();
  const a = t.about;

  return (
    <>
      <Seo
        title={a.seo.title}
        description={a.seo.description}
        path="/qui-suis-je"
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: a.breadcrumb, path: "/qui-suis-je" },
          ]),
        ]}
      />

      <section className="px-5 md:px-10 pt-36 md:pt-44 pb-20 md:pb-28" data-testid="about-hero">
        <div className="mx-auto max-w-7xl">
          <Breadcrumb items={[{ label: t.common.home, to: "/" }, { label: a.breadcrumb }]} />
          <div className="grid items-center gap-y-14 lg:grid-cols-[1.2fr_1fr] lg:gap-x-14 lg:gap-y-0">
            <div>
              <SectionTag>{a.tag}</SectionTag>
              <Reveal delay={0.1}>
                <h1 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-balance">
                  {a.h1}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-7 text-base md:text-lg leading-relaxed text-plum/85">
                  {a.p1}
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
              <div className="relative">
                <div className="overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(17,14,13,0.2)]">
                  <img
                    src="/assets/magali-clouzet-consultante-marketing-pme-antibes-cannes.png"
                    alt={a.photoAlt}
                    data-testid="about-portrait-img"
                    className="aspect-[4/5] w-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 rounded-2xl border border-ink/5 bg-white px-6 py-4 shadow-[0_20px_40px_-15px_rgba(17,14,13,0.15)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-terracotta-deep">{a.badgeLabel}</p>
                  <p className="mt-1 text-sm font-medium">{a.badgeCities}</p>
                </div>
              </div>
            </Reveal>
            <div className="lg:col-start-1 lg:row-start-2">
              <Reveal delay={0.25}>
                <p className="mt-5 text-base md:text-lg leading-relaxed text-plum/85">
                  <strong className="font-semibold text-ink">
                    {a.p2strong}
                  </strong>{" "}
                  {a.p2rest}
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-5 text-base md:text-lg leading-relaxed text-plum/85">
                  {a.p3a}{" "}
                  <strong className="font-semibold text-ink">{a.p3strong}</strong>{" "}
                  {a.p3b}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-20 md:py-28 bg-sand/50" data-testid="about-parcours">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <SectionTag>{a.parcours.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                {a.parcours.h2}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              {a.parcours.paragraphs.map((para, i) => (
                <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} text-base md:text-lg leading-relaxed text-plum/85`}>
                  {para}
                </p>
              ))}
            </Reveal>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {a.parcours.cards.map((card, i) => {
              const Icon = PARCOURS_ICONS[i];
              return (
              <Reveal key={card.title} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]">
                  <Icon className="w-6 h-6 text-terracotta-deep" strokeWidth={1.75} aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-plum/75">{card.text}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-20 md:py-28 bg-white" data-testid="about-approach">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <SectionTag>{a.approach.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                {a.approach.h2}
              </h2>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {a.approach.values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-ink/5 bg-cream/60 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]">
                  <Icon className="w-7 h-7 text-terracotta-deep" strokeWidth={1.75} aria-hidden="true" />
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-plum/75">{v.text}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-20 md:py-28" data-testid="about-who">
        <div className="mx-auto max-w-7xl grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionTag>{a.who.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-balance">
                {a.who.h2}
              </h2>
            </Reveal>
          </div>
          <div className="space-y-8">
            {a.who.profiles.map((prof, i) => (
              <Reveal key={prof.title} delay={i * 0.08}>
                <div className="flex gap-6 border-b border-ink/8 pb-8">
                  <span className="font-display text-3xl font-extrabold text-terracotta/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{prof.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-plum/75">{prof.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to={l("/services")}
                data-testid="about-cta-services"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-terracotta-deep"
              >
                {a.who.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABlock
        title={a.ctaBlock.title}
        ctaLabel={a.ctaBlock.ctaLabel}
        text={`${a.ctaBlock.textA} ${SITE.email} ${a.ctaBlock.textB}`}
      />
    </>
  );
}
